import { Injectable, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LAYOUT_COLORS } from "app/colors.brain.const";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import Swal, { SweetAlertIcon } from "sweetalert2";

//Servicio centralizado para las alertas de Swal
@Injectable({ providedIn: "root" })
export class CustomSwalService implements OnDestroy {

  private _unsubscribeAll: Subject<any>;

  constructor(private _translateService: TranslateService) {
    this._unsubscribeAll = new Subject();
   }


  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  //Muestra el modal de cargando
  showLoading(title?: string) {

    if(Swal.isLoading) Swal.close();

    this._translateService.get(['COMMON.ACTIONS.LOADING']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
      if (value) {
        Swal.fire({
          title: title ?? value['COMMON.ACTIONS.LOADING'],
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        Swal.showLoading();
      }
    });
  }

  /**
   * Muestra un modal preguntando si desea eliminar el item
   * @param deletingItem Indica el texto referenciando el item a eliminar
   * @returns Devuelve una promesa que tiene el resultado del modal, si es confirmado o no
   */
  showDeleteQuestion(deletingItem: string) {

    let _title: string = "";
    let _msj: string = "";
    let _btn_yes: string = "";
    let _btn_no: string = "";

    this._translateService.get(['COMMON.ACTIONS.DELETE_TITLE'
      , 'COMMON.ACTIONS.DELETE_TEXT'
      , 'COMMON.ACTIONS.CONFIRM_DELETE_TITLE'
      , 'COMMON.BUTTONS.CONFIRM_DELETE'
      , 'COMMON.BUTTONS.NO_KEEP']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {

        if (value) {
          _title = value['COMMON.ACTIONS.DELETE_TITLE'];
          _msj = `${deletingItem} ${value['COMMON.ACTIONS.DELETE_TEXT']}`;
          _btn_yes = value['COMMON.BUTTONS.CONFIRM_DELETE'];
          _btn_no = value['COMMON.BUTTONS.NO_KEEP']
        }

      });

    return Swal.fire({
      title: _title,
      text: _msj,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: LAYOUT_COLORS.swalConfirmButton,
      cancelButtonColor: LAYOUT_COLORS.swalCancelButton,
      confirmButtonText: _btn_yes,
      cancelButtonText: _btn_no,
    });
  }

  /**
   * Muestra el modal de pregunta para confirmación
   * @param title Indica el título del mensaje de confirmación
   * @param message Indica el mensaje a mostrar en la confirmación
   * @param confirmText Indica el texto a mostrar en el botón para confirmar
   * @param cancelText Indica el texto a mostrar en el botón para cancelar
   * @returns Devuelve una promesa que tiene el resultado del modal, si es confirmado o no
   */
  showQuestion(
    title: string,
    message: string = "",
    confirmText?: string,
    cancelText?: string
  ) {
    const swalWithBoostrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success mr-1",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });

    this._translateService.get(['COMMON.BUTTONS.YES', 'COMMON.BUTTONS.NO']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
      if (value) {
        if (!confirmText || confirmText.length === 0 ){
          confirmText = value['COMMON.BUTTONS.YES'];
        }
        if (!cancelText || cancelText.length === 0){
          cancelText = value['COMMON.BUTTONS.NO'];
        }
      }
    });

    return swalWithBoostrapButtons.fire({
      title: title,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
    });
  }

  isVisible(): boolean{
    return Swal.isVisible();
  }

  /**
   * Muestra el mensaje de éxito
   * @param {string} message Indica el mensaje a mostrar
   * @param {string} title Indica el titulo del mensaje
   * @returns
   */
  async showSuccessMsg(message: string, title?: string) {

    if (!title) {
      this._translateService.get(['COMMON.ACTIONS.SUCCESS_TITLE_MSJ']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
        title = value['COMMON.ACTIONS.SUCCESS_TITLE_MSJ'];
      });
    }

    return this.showMsg(title, message, "success");
  }

  /**
   * Muestra el mensaje de advertencia
   * @param {string} message Indica el mensaje a mostrar
   * @param {string} title Indica el titulo del mensaje
   * @returns
   */
  async showWarningMsg(message: string, title?: string) {

    if (!title) {
      this._translateService.get(['COMMON.ACTIONS.WARNING_TITLE_MSJ']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
        title = value['COMMON.ACTIONS.WARNING_TITLE_MSJ'];
      });
    }

    return this.showMsg(title, message, "warning");
  }

  /**
   * Muestra el mensaje de información
   * @param {string} message Indica el mensaje a mostrar
   * @param {string} title Indica el titulo del mensaje
   * @returns
   */
  async showInformationMsg(message: string, title?: string) {

    if (!title) {
      this._translateService.get(['COMMON.ACTIONS.INFORMATION_TITLE_MSJ']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
        title = value['COMMON.ACTIONS.INFORMATION_TITLE_MSJ'];
      });
    }

    return this.showMsg(title, message, "info");
  }


  /**
   * Muestra el mensaje de información con parametros 
   * @param message Indica el mensaje a mostrar (acepta etiquetas html)
   * @param icon Indica el ícono del mensaje que se mostrará
   * @param width define el ancho del modal de alerta
   * @param title Indica el titulo del mensaje
   */
  async showHtmlInformationMsg(message: string, icon: SweetAlertIcon, width: number, title?: string) {
    
    if(!title){
      this._translateService.get(['COMMON.ACTIONS.INFORMATION_TITLE_MSJ']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
        title = value['COMMON.ACTIONS.INFORMATION_TITLE_MSJ'];
      });
    }

    //return this.showMsg(title, message, "info");
    return this.showHtmlMsg(title, message, icon, width);
  }

  /**
   * Muestra el mensaje de error
   * @param {string} message Indica el mensaje a mostrar
   * @param {string} title Indica el titulo del mensaje
   * @returns
   */
  async showErrorMsg(message: string, title?: string) {

    if (!title) {
      this._translateService.get(['COMMON.ACTIONS.OOPS']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
        title = value['COMMON.ACTIONS.OOPS'];
      });
    }

    return this.showMsg(title, message, 'error');
  }

  /**
   * Muestra el modal de pregunta para confirmación sin traducción
   * @param title Indica el título del mensaje de confirmación
   * @param message Indica el mensaje a mostrar en la confirmación
   * @param confirmText Indica el texto a mostrar en el botón para confirmar
   * @param cancelText Indica el texto a mostrar en el botón para cancelar
   * @returns Devuelve una promesa que tiene el resultado del modal, si es confirmado o no
   */
  showQuestionNoTranslation(
    title: string,
    message: string = "",
    confirmText: string = "Sí",
    cancelText: string = "No"
  ) {
    const swalWithBoostrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success mr-1",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });

    return swalWithBoostrapButtons.fire({
      title: title,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
    });
  }

  /**
   * Muestra un aviso flotante arriba a la derecha.
   *
   * No usa SweetAlert2 a propósito: Swal maneja un único popup a la vez, así
   * que el toast desaparecía apenas alguien abría el modal de cargando (y al
   * revés, el toast se llevaba puesto al modal). Estos viven en su propio
   * contenedor, se apilan entre ellos y conviven con cualquier Swal abierto.
   *
   * @param {string} message Texto del aviso
   * @param {string} title Título opcional, en negrita sobre el mensaje
   * @param {SweetAlertIcon} icon Tipo de aviso; define el ícono y el color
   * @param {number} timer Milisegundos hasta que se cierra solo
   */
  showToastMsg(message: string, title?: string, icon: SweetAlertIcon = 'info', timer: number = 3000): void {

    const toast = document.createElement('div');
    toast.className = `brain-toast brain-toast-${icon}`;
    // Los errores interrumpen al lector de pantalla; el resto espera su turno
    toast.setAttribute('role', icon === 'error' ? 'alert' : 'status');

    const iconEl = document.createElement('span');
    iconEl.className = 'brain-toast-icon';
    iconEl.innerHTML = this._toastIcon(icon);

    const body = document.createElement('div');
    body.className = 'brain-toast-body';

    // textContent y no innerHTML: el mensaje puede traer datos del usuario
    if (title) {
      const titleEl = document.createElement('div');
      titleEl.className = 'brain-toast-title';
      titleEl.textContent = title;
      body.appendChild(titleEl);
    }

    if (message) {
      const textEl = document.createElement('div');
      textEl.className = 'brain-toast-text';
      textEl.textContent = message;
      body.appendChild(textEl);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'brain-toast-close';
    closeBtn.setAttribute('aria-label', this._translateService.instant('COMMON.BUTTONS.CLOSE'));
    closeBtn.innerHTML = this._toastIcon('close');

    const progress = document.createElement('span');
    progress.className = 'brain-toast-progress';
    progress.style.animationDuration = `${timer}ms`;

    toast.append(iconEl, body, closeBtn, progress);

    let timeoutId = 0;
    const dismiss = () => {
      if (!toast.parentElement) return;
      window.clearTimeout(timeoutId);
      toast.classList.remove('is-visible');
      // Se saca del DOM recién cuando termina la animación de salida
      window.setTimeout(() => toast.remove(), 250);
    };

    closeBtn.addEventListener('click', dismiss);
    timeoutId = window.setTimeout(dismiss, timer);

    this._toastHost().appendChild(toast);

    // Un frame de gracia para que la transición de entrada tenga de dónde salir
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
  }

  /** Contenedor de los toasts; se crea la primera vez que hace falta */
  private _toastHost(): HTMLElement {
    let host = document.querySelector('.brain-toast-host') as HTMLElement;

    if (!host) {
      host = document.createElement('div');
      host.className = 'brain-toast-host';
      document.body.appendChild(host);
    }

    return host;
  }

  /**
   * Ícono del aviso, en svg.
   *
   * Son los trazos de lucide escritos a mano: el toast se arma con DOM suelto
   * desde el servicio, así que no puede usar <lucide-icon> del IconsModule.
   *
   * @param kind Tipo de aviso, o 'close' para la cruz de cerrar
   */
  private _toastIcon(kind: SweetAlertIcon | 'close'): string {
    const paths = {
      success: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
      error: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
      warning: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
      info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
      question: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
      close: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'
    };

    const size = kind === 'close' ? 16 : 20;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      ${paths[kind] || paths.info}</svg>`;
  }

  close(): void {
    if (Swal.isLoading) Swal.close();
  }

  isLoading(): boolean {
    return Swal.isLoading();
  }

  /**
   * Función que sirve para abrir un mensaje de alerta
   * @param {string} title Indica el titulo del mensaje
   * @param {string} message Indica el mensaje a mostrar
   * @param {SweetAlertIcon} icon Indica el ícono del mensaje que se mostrará
   * @returns Devuelve una promesa
   */
  private async showMsg(title: string, message: string, icon: SweetAlertIcon) {
    if (Swal.isLoading) Swal.close();
    return Swal.fire({
      title: title,
      text: message,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
  }


  /**
   * Función que sirve para abrir un mensaje de alerta con parametros html
   * @param title Indica el titulo del mensaje
   * @param messageHtml Indica el mensaje a mostrar (admite etiquetas html)
   * @param icon Indica el ícono del mensaje que se mostrará
   * @returns Devuelve una promesa
   */
  private async showHtmlMsg(title: string, messageHtml: string, icon: SweetAlertIcon, width: number) {
    if (Swal.isLoading) Swal.close();
    return Swal.fire({
      title: title,
      text: messageHtml,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
      html: messageHtml,
      width: width,
    });
  }
}
